// src/admin/components/header/Header.jsx - Apartment Management System
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  RiMenu4Line,
  RiMoonLine,
  RiSunLine,
  RiNotificationLine,
  RiUserLine,
  RiSettings4Line,
  RiLogoutBoxLine,
  RiGlobalLine,
  RiArrowDownSLine
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';

const Header = ({ sidebarOpen, setSidebarOpen }) => {
  const { t, i18n } = useTranslation();
  const { isDarkMode, toggleDarkMode, isRTL, toggleRTL } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await api.get('/logs', { params: { limit: 20 } });
        if (response.data.success && response.data.logs) {
          // Filter out read events to keep notifications for actual changes
          const meaningfulLogs = response.data.logs.filter(log => !log.action.startsWith('view_')).slice(0, 5);

          // Format logs to notifications
          const formattedNotifs = meaningfulLogs.map(log => {
            let msg = '';
            // For logins, just show username
            if (log.action === 'user_login') {
              msg = `User ${log.entityName || 'logged in'}`;
              // For other actions, try to show entity name, otherwise show type and action
            } else {
              msg = log.entityName ? `${log.entityType}: ${log.entityName}` : `${log.entityType} updated`;
            }

            return {
              id: log.id,
              title: log.action.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
              message: msg,
              time: new Date(log.createdAt).toLocaleDateString(),
              unread: false
            };
          });
          setNotifications(formattedNotifs);
          setUnreadCount(0);
        }
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };

    if (user) {
      fetchNotifications();
    }
  }, [user]);

  const getTrialDaysLeft = () => {
    if (user?.subscriptionStatus === 'trial' && user?.trialEndsAt) {
      const endsAt = new Date(user.trialEndsAt);
      const now = new Date();
      const diffTime = endsAt - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays); // Ensure it doesn't go below 0
    }
    return null;
  };

  const trialDaysLeft = getTrialDaysLeft();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLanguageDropdownOpen(false);

    // Toggle RTL for Arabic and Kurdish
    if ((lng === 'ar' || lng === 'ku') && !isRTL) {
      toggleRTL();
    } else if (lng === 'en' && isRTL) {
      toggleRTL();
    }
  };

  // Function to get the display name of the current language
  const getCurrentLanguageDisplay = () => {
    switch (i18n.language) {
      case 'ar':
        return 'العربية';
      case 'ku':
        return 'کوردی';
      case 'en':
      default:
        return 'English';
    }
  };

  const closeAllDropdowns = () => {
    setProfileDropdownOpen(false);
    setLanguageDropdownOpen(false);
    setNotificationDropdownOpen(false);
  };

  return (
    <>
      <header className={`relative h-16 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'
        } border-b flex items-center justify-between px-4 md:px-6 w-full`}>

        {/* Left Section */}
        <div className="flex items-center space-x-3 md:space-x-4">
          {/* Mobile Sidebar Toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`p-2 rounded-lg md:hidden ${isDarkMode ? 'hover:bg-zinc-900 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
              } transition-colors`}
            aria-label="Toggle sidebar"
          >
            <RiMenu4Line size={20} />
          </button>

          {/* Desktop Sidebar Toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`hidden md:block p-2 rounded-lg ${isDarkMode ? 'hover:bg-zinc-900 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
              } transition-colors`}
            aria-label="Toggle sidebar"
          >
            <RiMenu4Line size={20} />
          </button>

          {/* Page Title / Logo */}
          <div className="flex items-center space-x-2">
            <img
              src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"}
              alt="QuadCores Logo"
              className="h-8 w-auto"
            />
            <h1 className={`text-lg md:text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
              Properties
            </h1>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-2 md:space-x-4">


          {/* Trial Badge — hidden for custom1 (SUBSCRIPTION_BYPASS) */}


          {/* Language Selector */}
          <div className="relative">
            <button
              onClick={() => {
                setLanguageDropdownOpen(!languageDropdownOpen);
                setProfileDropdownOpen(false);
                setNotificationDropdownOpen(false);
              }}
              className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-zinc-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                } flex items-center space-x-1 transition-colors`}
            >
              <RiGlobalLine size={20} />
              <span className="text-sm font-medium hidden sm:block">
                {getCurrentLanguageDisplay()}
              </span>
              <RiArrowDownSLine size={16} />
            </button>

            {languageDropdownOpen && (
              <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-48 rounded-lg shadow-lg ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'
                } border z-50`}>
                <div className="py-1">
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${i18n.language === 'en'
                      ? isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-50 text-red-600'
                      : isDarkMode ? 'text-gray-300 hover:bg-zinc-800' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => changeLanguage('ar')}
                    className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${i18n.language === 'ar'
                      ? isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-50 text-red-600'
                      : isDarkMode ? 'text-gray-300 hover:bg-zinc-800' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    العربية
                  </button>
                  <button
                    onClick={() => changeLanguage('ku')}
                    className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${i18n.language === 'ku'
                      ? isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-50 text-red-600'
                      : isDarkMode ? 'text-gray-300 hover:bg-zinc-800' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    کوردی
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleDarkMode}
            className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-zinc-900 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
              } transition-colors`}
            aria-label="Toggle theme"
          >
            {isDarkMode ? <RiSunLine size={20} /> : <RiMoonLine size={20} />}
          </button>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => {
                setNotificationDropdownOpen(!notificationDropdownOpen);
                setProfileDropdownOpen(false);
                setLanguageDropdownOpen(false);
              }}
              className={`relative p-2 rounded-lg ${isDarkMode ? 'hover:bg-zinc-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                } transition-colors`}
              aria-label="Notifications"
            >
              <RiNotificationLine size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-zinc-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {notificationDropdownOpen && (
              <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-80 rounded-lg shadow-lg ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'
                } border z-50`}>
                <div className={`p-4 border-b ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'}`}>
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('notifications')}
                  </h3>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 border-b ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} hover:bg-opacity-50 transition-colors ${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-50'
                        } ${notification.unread ? isDarkMode ? 'bg-red-900/10' : 'bg-zinc-50' : ''
                        }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-2 h-2 rounded-full mt-2 ${notification.unread ? 'bg-zinc-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                          }`} />
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                            {notification.title}
                          </p>
                          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'
                            }`}>
                            {notification.message}
                          </p>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'
                            }`}>
                            {notification.time}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-4">
                  <button className={`w-full text-center text-sm transition-colors ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-500'
                    }`}>
                    {t('view_all_notifications')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User Profile Dropdown */}
          <div className="relative">
            <button
              onClick={() => {
                setProfileDropdownOpen(!profileDropdownOpen);
                setLanguageDropdownOpen(false);
                setNotificationDropdownOpen(false);
              }}
              className={`flex items-center space-x-2 p-1 rounded-lg ${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'
                } transition-colors`}
            >
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-md">
                <span className="font-bold text-white text-sm">
                  {user?.firstName?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                  {user?.firstName} {user?.lastName}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'
                  }`}>
                  {user?.role === 'admin' ? t('administrator') :
                   user?.role === 'front_desk' ? t('front_desk') :
                   user?.role === 'accountant' ? t('accountant') :
                   user?.role === 'sales' ? t('sales_role') : t('front_desk')}
                </p>
              </div>
              <RiArrowDownSLine className={`${isDarkMode ? 'text-zinc-400' : 'text-gray-500'
                }`} size={16} />
            </button>

            {profileDropdownOpen && (
              <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-48 rounded-lg shadow-lg ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'
                } border z-50`}>
                <div className="py-1">
                  <button
                    onClick={() => {
                      navigate('/admin/settings/profile');
                      setProfileDropdownOpen(false);
                    }}
                    className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${isDarkMode ? 'text-gray-300 hover:bg-zinc-800' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <RiUserLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={16} />
                    {t('profile')}
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin/settings');
                      setProfileDropdownOpen(false);
                    }}
                    className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${isDarkMode ? 'text-gray-300 hover:bg-zinc-800' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <RiSettings4Line className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={16} />
                    {t('settings')}
                  </button>
                  <hr className={`my-1 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'}`} />
                  <button
                    onClick={handleLogout}
                    className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${isDarkMode ? 'text-red-400 hover:bg-zinc-800' : 'text-red-600 hover:bg-gray-100'
                      }`}
                  >
                    <RiLogoutBoxLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={16} />
                    {t('logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Click outside handlers */}
      {(profileDropdownOpen || languageDropdownOpen || notificationDropdownOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={closeAllDropdowns}
        />
      )}
    </>
  );
};

export default Header;